import { apiRequest } from "@/data/api/client";
import { payloadToBusiness, type BusinessPayload } from "@/data/mappers/partnerBusiness.mapper";
import type { BusinessProfileInput } from "@/domain/business/schema";
import type { Business } from "@/domain/business/types";

export interface BusinessSave {
  /** The address the API was asked to mail its sample confirmation request to,
   *  which is the only thing the partner can be told to watch their inbox for. */
  sampleSentTo: string | null;
}

function siteBody(input: BusinessProfileInput) {
  return {
    street: input.addressStreet,
    city: input.addressCity,
    zip: input.addressCode,
    latitude: input.locationLatitude,
    longitude: input.locationLongitude,
  };
}

export const partnerBusinessRepo = {
  /** One partner, one business in v2 — the token decides which, and the portal
   *  edits the first the API answers with. */
  async get(): Promise<Business | null> {
    const payloads = await apiRequest<BusinessPayload[]>("/v1/partner/business");
    const [first] = payloads;
    return first ? payloadToBusiness(first) : null;
  },

  /** The create endpoint takes the name, the address and the notification
   *  address only — the phone and the discount limit follow in a second
   *  request, as they did before the API owned this. */
  async create(input: BusinessProfileInput): Promise<BusinessSave> {
    const { id } = await apiRequest<{ id: number }>("/v1/partner/business", {
      method: "POST",
      body: { name: input.name, ...siteBody(input), notificationEmail: input.notificationEmail },
    });
    const carriesMore = input.phone !== null || input.discountUsesPerCustomer !== null;
    if (carriesMore) {
      await apiRequest(`/v1/partner/business/${id}`, {
        method: "PATCH",
        body: {
          phone: input.phone,
          discountUsesPerCustomer: input.discountUsesPerCustomer,
          discountUsesPeriodDays: input.discountUsesPeriodDays,
        },
      });
    }
    return { sampleSentTo: null };
  },

  /** Two requests, because the address is the site's row and the rest is the
   *  business's. Both halves of the discount limit always travel together: the
   *  API refuses one without the other, and the pair is how a limit is cleared.
   *
   *  The notification address is sent only when the partner changed it — the
   *  PATCH mails a sample confirmation request to every address it receives, so
   *  including an unchanged one would mail it on every save. Emptying the field
   *  is a change like any other: an omitted key is how "unchanged" is encoded,
   *  so swallowing the empty one would let the API refuse nothing and the
   *  partner be told a cleared address was saved. */
  async update(business: Business, input: BusinessProfileInput): Promise<BusinessSave> {
    const emailChanged = input.notificationEmail !== business.notificationEmail;
    await apiRequest(`/v1/partner/business/${business.id}`, {
      method: "PATCH",
      body: {
        name: input.name,
        phone: input.phone,
        ...(emailChanged ? { notificationEmail: input.notificationEmail } : {}),
        discountUsesPerCustomer: input.discountUsesPerCustomer,
        discountUsesPeriodDays: input.discountUsesPeriodDays,
      },
    });
    await apiRequest(`/v1/partner/sites/${business.defaultSiteId}`, {
      method: "PATCH",
      body: siteBody(input),
    });
    return { sampleSentTo: emailChanged ? input.notificationEmail : null };
  },
};
